/**
 * AppRoutes — thin shell composing modular route sections.
 *
 * Section order is intentional (React Router first-match). See docs/ROUTES.md.
 *
 * Route nodes from sections are flattened into direct <Route> children so
 * matching does not depend on Fragment unwrapping across section helpers.
 */
import React, { Suspense, Children, isValidElement } from "react";
import { Routes, Navigate } from "react-router-dom";
import { Box } from "@mui/material";
import { CLINICAL_FONT_FAMILY } from "@/components/shared/constants";
import type { AppRoutesProps, RouteCtx } from "./routeContext";
import { getPrimaryRole, canAccessRbqmCenter } from "./helpers";
import { LoadingSpinner, RouteErrorBoundary } from "./shell";
import { MenuAccessGuard } from "./MenuAccessGuard";
import { collectActiveMenuPaths } from "./menuAccess";
import {
  dashboardShellRoutes,
  tenantRoutes,
  piRoutes,
  studyCoordinatorRoutes,
  craRoutes,
  ctaRoutes,
  dataManagerRoutes,
  sponsorRoutes,
  siteAdminRoutes,
  projectManagerRoutes,
  sharedMonitoringRoutes,
  fallbackAliasRoutes,
  consolidatedRoutes,
  defaultRoutes,
} from "./sections";

export type { AppRoutesProps } from "./routeContext";

/** Flatten section fragments/arrays into a list of Route elements. */
function flattenRouteNodes(...nodes: React.ReactNode[]): React.ReactElement[] {
  const routes: React.ReactElement[] = [];

  const visit = (node: React.ReactNode) => {
    Children.forEach(node, (child) => {
      if (!isValidElement(child)) {
        return;
      }
      if (child.type === React.Fragment) {
        visit((child.props as { children?: React.ReactNode }).children);
        return;
      }
      routes.push(child);
    });
  };

  for (const node of nodes) {
    visit(node);
  }

  return routes;
}

export const AppRoutes: React.FC<AppRoutesProps> = ({
  isAuthenticated,
  user,
  onLogout,
  selectedStudyId,
  availableStudies = [],
  userStudyRoles = {},
  menu = [],
  menuReady = true,
  sx,
}) => {
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  const userRole = getPrimaryRole(user, selectedStudyId, userStudyRoles);
  const hasRbqmAccess = canAccessRbqmCenter(userRole);
  const currentStudy = availableStudies.find((s) => s.id === selectedStudyId);
  const menuPaths = collectActiveMenuPaths(menu);
  const homePath = menuPaths.has("/dashboard")
    ? "/dashboard"
    : ([...menuPaths][0] ?? "/dashboard");

  const ctx: RouteCtx = {
    user,
    onLogout,
    userRole,
    selectedStudyId,
    availableStudies,
    userStudyRoles,
    hasRbqmAccess,
    currentStudy,
  };

  const routes = flattenRouteNodes(
    dashboardShellRoutes(ctx),
    tenantRoutes(ctx),
    piRoutes(ctx),
    studyCoordinatorRoutes(ctx),
    craRoutes(ctx),
    ctaRoutes(ctx),
    dataManagerRoutes(ctx),
    sponsorRoutes(ctx),
    siteAdminRoutes(ctx),
    projectManagerRoutes(ctx),
    sharedMonitoringRoutes(ctx),
    fallbackAliasRoutes(ctx),
    consolidatedRoutes(ctx),
    defaultRoutes(),
  );

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        minHeight: "100vh",
        backgroundColor: "#F8FAFC",
        fontFamily: CLINICAL_FONT_FAMILY,
        ...sx,
      }}
    >
      <Suspense fallback={<LoadingSpinner />}>
        <MenuAccessGuard
          menu={menu}
          menuReady={menuReady}
          roleLabel={userRole}
          homePath={homePath}
        >
          <RouteErrorBoundary>
            <Routes>{routes}</Routes>
          </RouteErrorBoundary>
        </MenuAccessGuard>
      </Suspense>
    </Box>
  );
};

export default AppRoutes;
